'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { signIn } from 'next-auth/react'
import { loginSchema, type LoginInput } from '@/lib/validations/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { toast } from 'sonner'
import { UserCircle, Shield, ChevronDown, ChevronUp, Utensils } from 'lucide-react'

export function LoginForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [quickLoginOpen, setQuickLoginOpen] = useState(false)
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') ?? '/dashboard'

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    mode: 'onChange',
  })

  /**
   * After a successful credentials response, hard-navigate so the session cookie is committed
   * before the next document load (fixes embedded / automation browsers; `router.push` alone can lose the cookie).
   * Keep `redirect: false` first so wrong passwords still show a toast instead of a blind redirect.
   */
  async function performCredentialsSignIn(email: string, password: string) {
    setIsLoading(true)
    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
        callbackUrl,
      })

      if (result?.error) {
        toast.error('Invalid email or password')
        return
      }

      if (result?.ok) {
        window.location.assign(result.url ?? '/')
        return
      }

      toast.error('Invalid email or password')
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  /** Dev Quick Login: fill fields for visibility and sign in in one step (avoids RHF/automation gaps). */
  const quickLogin = async (email: string, password: string) => {
    form.setValue('email', email, { shouldValidate: true, shouldDirty: true })
    form.setValue('password', password, { shouldValidate: true, shouldDirty: true })
    await performCredentialsSignIn(email, password)
  }

  async function onSubmit(data: LoginInput) {
    await performCredentialsSignIn(data.email, data.password)
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Sign In</CardTitle>
        <CardDescription>
          Enter your credentials to access your account
        </CardDescription>
      </CardHeader>

      {/* Quick Login - Development Only */}
      {process.env.NODE_ENV === 'development' && (
        <div className="px-6 pb-4">
          <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/50 overflow-hidden">
            {/* Collapsible header */}
            <button
              type="button"
              onClick={() => setQuickLoginOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/80 transition-colors"
            >
              <p className="text-xs font-medium text-muted-foreground">
                🚀 Quick Login (Dev Mode)
              </p>
              {quickLoginOpen
                ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>

            {/* Collapsible body */}
            {quickLoginOpen && (
              <div className="px-4 pb-4 flex flex-col gap-2">
                {/* Global Admin */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  disabled={isLoading}
                  onClick={() => void quickLogin('admin@encore-ai.com', 'EncoreAdmin2025!')}
                >
                  <Shield className="mr-2 h-4 w-4 shrink-0 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold text-xs">Global Admin</div>
                    <div className="text-xs text-muted-foreground">admin@encore-ai.com</div>
                  </div>
                </Button>

                {/* Lomontes brand demo tenant */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  disabled={isLoading}
                  onClick={() => void quickLogin('owner@goldenfork.com', 'GoldenFork2025!')}
                >
                  <Utensils className="mr-2 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="text-left">
                    <div className="font-semibold text-xs">Lomontes Italian Restaurant &amp; Bar</div>
                    <div className="text-xs text-muted-foreground">owner@goldenfork.com</div>
                  </div>
                </Button>

                {/* Bistro Moderne */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  disabled={isLoading}
                  onClick={() => void quickLogin('manager@bistromoderne.com', 'BistroMod2025!')}
                >
                  <UserCircle className="mr-2 h-4 w-4 shrink-0 text-green-600" />
                  <div className="text-left">
                    <div className="font-semibold text-xs">Bistro Moderne</div>
                    <div className="text-xs text-muted-foreground">manager@bistromoderne.com</div>
                  </div>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-between">
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        Remember me
                      </FormLabel>
                    </div>
                  </FormItem>
                )}
              />
              <Link
                href="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link href="/register" className="text-primary hover:underline">
                Register
              </Link>
            </p>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
